import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import { Pagination } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useScrollToTop } from 'hooks/useScrollToTop';
import { STATUS_LOADING, ISO_DATE_FORMAT } from 'const';
import {
  setLeaveModalMainData,
  toggleVacationModalVisibility,
} from 'modules/SalaryModule/Vacations/actions';
import {
  changeEmployeesFilters,
  resetEmployeesFilters,
  setDateRange,
  setEmployeeSchedule,
  setEmployeesSearch,
  fetchEmployeesRequest,
  downloadEmployeePayslips,
  downloadEmployeesTableXlsx,
  setEmployeesPage,
  fetchEmployeesDebouncedRequest,
} from 'modules/SalaryModule/Employees/actions';
import { selectUserCurrentCompany } from 'modules/loggedUserInfo/selectors';
import { FiltersToolbar } from 'components/SalaryComponents/FiltersToolbar';
import { Spinner } from 'components/Spinner';
import { getEmployeeTableCsvCommonFilter } from 'modules/SalaryModule/Employees/utils';
import {
  selectClassificationOptions,
  selectContractOptions,
  selectDateRange,
  selectEmployeesStatus,
  selectFilteredEmployees,
  selectLastSyncTime,
  selectLastSyncingStatus,
  selectFilters,
  selectIsFilterChosen,
  selectNatureEmploymentOptions,
  selectSearchTerm,
  selectEmployeePagination,
} from 'modules/SalaryModule/Employees/selectors';
import { useSimpleMedia } from 'hooks/useMedia';
import {
  selectCalendarDateFilter,
  selectEmployeesWithAbsences,
} from 'modules/SalaryModule/Calendar/selectors';
import { getTablePaginationRange } from 'helpers/salaryHelpers/calendar';
import { getCalendarAbsencesRequest } from 'modules/SalaryModule/Calendar/actions';
import { EMPLOYEE_HEADER } from 'const/translations/SalaryEmployeePage';
import { Button } from 'components/ui';
import { EmployeePageType } from 'const/SalaryMainPage';
import ButtonStyles from 'components/SalaryComponents/FiltersToolbar/FiltersToolbar.module.css';
import Styles from './EmployeesPage.module.css';
import { EmployeeTable } from './EmployeeTable';
import { EmployeeList } from './EmployeeList';
import { NoDataFound } from './NoDataFound';
import { TimeTableModal } from './TimeTableModal';

const SCREEN_WIDTH_BREAKPOINT_VALUE = 796;
const SCREEN_WIDTH_BREAKPOINT_MEDIA = `(max-width: ${SCREEN_WIDTH_BREAKPOINT_VALUE}px)`;
const { BUTTONS_LABELS } = EMPLOYEE_HEADER;

export const EmployeesPage = memo(() => {
  const dispatch = useDispatch();
  const [scheduleId, setScheduleId] = useState(null);
  const [scheduleActiveMember, setScheduleActiveMember] = useState(null);
  const [isTimeModalVisible, setTimeModalVisibility] = useState(false);
  const loadingStatus = useSelector(selectEmployeesStatus);
  const natureEmploymentsOptions = useSelector(selectNatureEmploymentOptions);
  const classificationsOptions = useSelector(selectClassificationOptions);
  const contractsOptions = useSelector(selectContractOptions);
  const isMobileVersion = useSimpleMedia(SCREEN_WIDTH_BREAKPOINT_MEDIA);
  const isFiltersChosen = useSelector(selectIsFilterChosen);
  const employees = useSelector(selectFilteredEmployees);
  const lastSyncTime = useSelector(selectLastSyncTime);
  const lastSyncingStatus = useSelector(selectLastSyncingStatus);
  const pagination = useSelector(selectEmployeePagination);

  const filters = useSelector(selectFilters);
  const searchTerm = useSelector(selectSearchTerm);
  const dateRange = useSelector(selectDateRange);
  const {
    id: companyId,
    managedSilae,
    payEnabled,
  } = useSelector(selectUserCurrentCompany);

  useScrollToTop();

  const hasFilters = useMemo(
    () => Object.values(filters).filter((item) => item !== '').length > 0,
    [filters]
  );

  const onSelectChange = useCallback(
    (name, value) => {
      dispatch(
        changeEmployeesFilters({
          [name]: value || '',
        })
      );
    },
    [dispatch]
  );

  const onSearchChange = useCallback(
    (e) => {
      dispatch(setEmployeesSearch(e.target.value));

      dispatch(
        fetchEmployeesDebouncedRequest({
          pageType: EmployeePageType.Employees,
          search: e.target.value,
        })
      );
    },
    [dispatch]
  );

  const onSubmit = useCallback(
    (values) => {
      dispatch(changeEmployeesFilters(values));
    },
    [dispatch]
  );

  const handleEmployeeScheduleChange = useCallback(() => {
    if (scheduleId && scheduleActiveMember && companyId) {
      dispatch(
        setEmployeeSchedule({
          companyId,
          scheduleId,
          employeeId: scheduleActiveMember.employeeid,
        })
      );
      setTimeModalVisibility(false);
    }
  }, [dispatch, scheduleId, scheduleActiveMember, companyId]);

  const resetFilters = useCallback(() => {
    dispatch(resetEmployeesFilters());
  }, [dispatch]);

  const resetSearch = useCallback(() => {
    dispatch(setEmployeesSearch(''));
  }, [dispatch]);

  const onRangeChange = useCallback(
    ([startDate, endDate]) => {
      dispatch(setDateRange({ startDate, endDate }));
    },
    [dispatch]
  );

  const openTimeModal = useCallback(
    (e) => {
      const { employeeid } = e.currentTarget.dataset;
      if (employeeid) {
        const employeeData = employees.find(
          (employee) => employee.employeeid === employeeid
        );
        setScheduleActiveMember(employeeData);
        setScheduleId(employeeData.schedule);
      }
      setTimeModalVisibility(true);
    },
    [employees]
  );

  const filteredEmployees = useSelector(selectEmployeesWithAbsences);

  const monthFilter = useSelector(selectCalendarDateFilter);
  useEffect(() => {
    dispatch(getCalendarAbsencesRequest({ date: monthFilter, companyId }));
  }, [monthFilter.format('YYYY-MM-DD'), dispatch, companyId]);

  useEffect(() => {
    if (dateRange.startDate) {
      dispatch(
        fetchEmployeesRequest({
          companyId,
          pageType: EmployeePageType.Employees,
        })
      );
    }
  }, [
    companyId,
    dispatch,
    dateRange.startDate,
    dateRange.endDate,
    payEnabled,
    filters,
  ]);

  const openVacationModal = useCallback(
    (e) => {
      const { reason, employeeid } = e.currentTarget.dataset;
      if (employees) {
        const employeeData = employees.find(
          (employee) => employee.employeeid === employeeid
        );

        if (employeeData) {
          dispatch(
            setLeaveModalMainData({
              employee: employeeData,
              otherEmployees: filteredEmployees,
              type: reason,
            })
          );
          dispatch(toggleVacationModalVisibility());
        }
      }
    },
    [dispatch, employees, filteredEmployees]
  );

  const downloadEmployeePayslipsHandler = useCallback(
    (employeeId) => () => {
      const { startDate, endDate } = dateRange;
      dispatch(
        downloadEmployeePayslips({
          companyId,
          employeeId,
          startDate: startDate.format(ISO_DATE_FORMAT),
          endDate: endDate.format(ISO_DATE_FORMAT),
        })
      );
    },
    [companyId, dateRange, dispatch]
  );

  const closeTimeModal = useCallback(() => {
    setTimeModalVisibility(false);
  }, []);

  const renderEmployeesList = useCallback(() => {
    const isNotDate = !dateRange.startDate && !dateRange.endDate;

    if (isFiltersChosen && employees.length === 0) {
      return <NoDataFound onResetFilters={resetFilters} />;
    }

    return isMobileVersion ? (
      <EmployeeList
        employees={!isNotDate && employees}
        openVacationModal={openVacationModal}
        openTimeModal={openTimeModal}
        managedSilae={managedSilae}
        downloadEmployeePayslipsHandler={downloadEmployeePayslipsHandler}
        loading={loadingStatus === STATUS_LOADING}
      />
    ) : (
      <EmployeeTable
        employees={!isNotDate && employees}
        openVacationModal={openVacationModal}
        openTimeModal={openTimeModal}
        managedSilae={managedSilae}
        downloadEmployeePayslipsHandler={downloadEmployeePayslipsHandler}
        loading={loadingStatus === STATUS_LOADING}
      />
    );
  }, [
    isFiltersChosen,
    employees,
    loadingStatus,
    isMobileVersion,
    openVacationModal,
    openTimeModal,
    managedSilae,
    downloadEmployeePayslipsHandler,
    resetFilters,
    dateRange,
  ]);

  const downloadTableXlsxHandler = useCallback(() => {
    dispatch(
      downloadEmployeesTableXlsx({
        companyId,
        filter: getEmployeeTableCsvCommonFilter(filters, searchTerm, dateRange),
      })
    );
  }, [filters, searchTerm, dateRange, dispatch, companyId]);

  const setCurrentPage = useCallback(
    (page) => {
      dispatch(setEmployeesPage(page));
      dispatch(fetchEmployeesRequest({ pageType: EmployeePageType.Employees }));
    },
    [dispatch]
  );

  const tablePagination = useMemo(
    () => ({
      showTotal: (total, range) =>
        getTablePaginationRange(range[0], range[1], total),
      responsive: true,
      pageSize: pagination.limit,
      hideOnSinglePage: true,
      size: isMobileVersion ? 'small' : 'default',
      defaultCurrent: 1,
      current: pagination.currentPage,
      onChange: setCurrentPage,
      total: pagination.total,
      showSizeChanger: false,
    }),
    [
      pagination.limit,
      isMobileVersion,
      pagination.currentPage,
      pagination.total,
      setCurrentPage,
    ]
  );

  return (
    <div className={Styles.container}>
      <FiltersToolbar
        natureEmploymentsOptions={natureEmploymentsOptions}
        classificationsOptions={classificationsOptions}
        contractsOptions={contractsOptions}
        filters={filters}
        searchTerm={searchTerm}
        dateRange={dateRange}
        hasFilters={hasFilters}
        onSelectChange={onSelectChange}
        onSearchChange={onSearchChange}
        onSubmit={onSubmit}
        onRangeChange={onRangeChange}
        resetSearch={resetSearch}
        companyId={companyId}
        managedSilae={managedSilae}
        lastSyncTime={lastSyncTime}
        lastSyncingStatus={lastSyncingStatus}
      />
      {loadingStatus === STATUS_LOADING ? (
        <div style={{ marginBottom: '357px' }}>
          <Spinner />
        </div>
      ) : (
        renderEmployeesList()
      )}

      <div className={Styles.buttonWrapper}>
        <Button
          sizeAuto
          variant="transparent"
          className={ButtonStyles.downloadButton}
          onClick={downloadTableXlsxHandler}
        >
          <DownloadOutlined className={ButtonStyles.downloadIcon} />
          {BUTTONS_LABELS.DOWNLOAD_LONG}
        </Button>

        <Pagination {...tablePagination} />
      </div>

      <TimeTableModal
        isVisible={isTimeModalVisible}
        onClose={closeTimeModal}
        onSubmit={handleEmployeeScheduleChange}
        scheduleId={scheduleId}
        setScheduleId={setScheduleId}
        scheduleActiveMember={scheduleActiveMember}
      />
    </div>
  );
});
